import React, { useState } from "react";

import { useDomain } from "context/DomainContext";
import { useOptionContext } from "context/OptionContext";
import { useCurrentUser } from "context/UserContext";
import { addDoc, collection } from "firebase/firestore";
import { db } from "firebaseConfig";
import { t } from "i18next";
import { ClientDetails } from "pages/QuotationDetails";
import { useNavigate } from "react-router-dom";
import {
  AttachmentAdjustments,
  Line,
  MaterialType,
  Province,
} from "types/canvasTypes";
import "./ClientForm.css";

const initialFormState: ClientDetails = {
  name: "",
  email: "",
  phone: "",
  address: "",
  note: "",
  chalkLine: false,
};

type Props = {
  lines: Line[];
  material: MaterialType;
  attachmentAdjustments: AttachmentAdjustments;
  selectedLength: String;
  scale: number;
  includeAttachement: boolean;
};

const ClientForm = (props: Props) => {
  const [formData, setFormData] = useState<ClientDetails>(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false); // Add state variable for submitting status
  const { chalkLine, province, gapSize } = useOptionContext();
  const navigate = useNavigate();
  const { isReseller } = useDomain();

  const { currentUser, signIn } = useCurrentUser();
  const isLoggedIn = !!currentUser;
  const canSave = isLoggedIn || isReseller;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canSave || isSubmitting) return;

    setIsSubmitting(true); // Set submitting status to true

    try {
      const docRef = await addDoc(collection(db, "quotations"), {
        ...formData,
        lines: JSON.stringify(props.lines),
        material: props.material,
        selectedLength: props.selectedLength,
        scale: props.scale,
        includeAttachement: props.includeAttachement,
        attachmentAdjustments: props.attachmentAdjustments,
        date: new Date(),
        createdBy: currentUser?.email || "guest",
        chalkLine,
        province,
        gapSize,
      });
      navigate(`/quotations/${docRef.id}`);
    } catch (e) {}

    setIsSubmitting(false); // Set submitting status back to false
  };

  const handleLogin = async () => {
    if (isSubmitting) return;
    try {
      await signIn();
    } catch (e) {}
  };

  const checkDisabled = formData.email === "" || isSubmitting;

  return (
    <form className="client-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="client-name">{t("name")}</label>
        <input
          id="client-name"
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
        />
      </div>
      <div className="form-group">
        <label htmlFor="client-email">{t("email")}</label>
        <input
          id="client-email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
        />
      </div>
      <div className="form-group">
        <label htmlFor="client-phone">{t("phoneNumber")}</label>
        <input
          id="client-phone"
          type="text"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
        />
      </div>
      <div className="form-group">
        <label htmlFor="client-address">{t("address")}</label>
        <input
          id="client-address"
          type="text"
          name="address"
          value={formData.address}
          onChange={handleChange}
        />
      </div>
      {province === Province.Ontario && (
        <div className="form-group">
          <label htmlFor="client-note">{t("notes")}</label>
          <input
            id="client-note"
            type="text"
            name="note"
            value={formData.note}
            onChange={handleChange}
          />
        </div>
      )}
      <button
        type={canSave ? "submit" : "button"}
        disabled={canSave ? checkDisabled : isSubmitting}
        onClick={canSave ? undefined : handleLogin}
        className="edit-button"
      >
        {canSave ? t("save") : t("login")}
      </button>
    </form>
  );
};

export default ClientForm;
